import React, { useState, useContext, useEffect } from 'react';
import { ShoppingCart } from 'shared/build/types';
import { ShoppingCartUI, ShoppingCartItemUI } from '../util/types';
import { customFetch, convertShoppingCartToUI } from '../util/functions';
import { toast } from 'react-toastify';

type ShoppingCartContextT = {
  shoppingCart: ShoppingCartUI;
  addItem: (item: Omit<ShoppingCartItemUI, 'unitPrice'>) => Promise<boolean>;
  removeItem: (productId: string) => Promise<boolean>;
  updateCart: () => Promise<void>;
};

const ShoppingCartContext = React.createContext<
  ShoppingCartContextT | undefined
>(undefined);

export const ShoppingCartProvider = ({
  children,
  initialShoppingCart,
}: {
  children: React.ReactNode;
  initialShoppingCart?: ShoppingCart;
}) => {
  const [shoppingCart, setShoppingCart] = useState<ShoppingCartUI>({
    createdAt: new Date(),
    id: '',
    items: {},
    revision: '',
    tenantId: '',
    updatedAt: new Date(),
    userId: '',
    active: false,
  });

  useEffect(() => {
    if (!initialShoppingCart) return;

    setShoppingCart(convertShoppingCartToUI(initialShoppingCart));
  }, [initialShoppingCart]);

  const addItem = async (
    itemData: Omit<ShoppingCartItemUI, 'unitPrice'>
  ): Promise<boolean> => {
    if (itemData.quantity < 0 || (!shoppingCart.items[itemData.productId] && itemData.quantity <= 0)) {
      toast.error('A quantidade precisa ser maior que 0.');
      return false;
    }

    const response = await customFetch('/api/website/cart/item', {
      body: JSON.stringify(itemData),
      method: 'POST',
    });

    if (!response.ok) {
      if (response.status >= 400 && response.status < 500) {
        return false;
      }
      throw new Error('Failed to insert the product');
    }

    const updatedCart: ShoppingCart = await response.json();
    setShoppingCart(convertShoppingCartToUI(updatedCart));

    return true;
  };

  const removeItem = async (productId: string): Promise<boolean> => {
    if (!shoppingCart.items[productId]) {
      throw new Error('The item is not in the cart!');
    }

    const response = await customFetch(`/api/website/cart/item/${productId}`, {
      method: 'DELETE',
    });

    if (!response.ok) throw new Error('Failed to delete the item!');

    const updatedCart: ShoppingCart = await response.json();
    setShoppingCart(convertShoppingCartToUI(updatedCart));

    return true;
  };

  const updateCart = async (): Promise<void> => {
    const response = await customFetch('/api/website/cart');
    if (!response.ok) throw new Error('Failed to fetch the cart!');

    const updatedCart: ShoppingCart = await response.json();

    if (updatedCart.revision !== shoppingCart.revision) {
      setShoppingCart(convertShoppingCartToUI(updatedCart));
    }
  };

  return (
    <ShoppingCartContext.Provider
      value={{ shoppingCart, addItem, removeItem, updateCart }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
};

export const useShoppingCart = (): ShoppingCartContextT => {
  const value = useContext(ShoppingCartContext);

  if (value === undefined)
    throw new Error('The Shopping Cart context had not been initialized.');

  return value;
};
